import { useEffect, useState, useRef } from "react";
import {
  useAdminTickets,
  useAdminTicket,
  useAdminReplyTicket,
  useAdminCloseTicket,
  useAdminReopenTicket,
  useAdminMe,
  useAdminPlans,
} from "../hooks/useApi";
import { supportApi } from "../services/api";
import { toPersianDigits, formatTomanInt } from "../utils/format";
import TicketMessageContent from "../components/TicketMessageContent";
import "../admin-theme.css";

const statusBadge: Record<string, { cls: string; label: string }> = {
  open: { cls: "admin-badge-warning", label: "باز" },
  answered: { cls: "admin-badge-success", label: "پاسخ داده شده" },
  closed: { cls: "admin-badge-info", label: "بسته شده" },
};

const priorityLabel: Record<string, string> = {
  low: "کم",
  medium: "متوسط",
  high: "زیاد",
};

const categoryLabel: Record<string, string> = {
  general: "عمومی",
  billing: "مالی و پرداخت",
  technical: "فنی",
  api: "API",
  account: "حساب کاربری",
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "هم‌اکنون";
  if (minutes < 60) return `${minutes.toLocaleString("fa-IR")} دقیقه پیش`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours.toLocaleString("fa-IR")} ساعت پیش`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days.toLocaleString("fa-IR")} روز پیش`;
  return new Date(iso).toLocaleDateString("fa-IR");
}

/** Priority for the user column: full name, then phone, then email. */
function userDisplayLabel(user: Record<string, unknown>): string {
  const fullName = [user.user_name, user.user_family_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fullName || (user.user_phone as string) || (user.user_email as string) || "—";
}

function UserInfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0" style={{ color: "var(--admin-text-tertiary)" }}>
        {label}
      </span>
      <span className="text-left font-medium break-all" style={{ color: "var(--admin-text-primary)" }} dir="auto">
        {value || "—"}
      </span>
    </div>
  );
}

export default function AdminTickets() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("newest");
  const { data: tickets, isLoading } = useAdminTickets(
    search,
    selectedPlan ? Number(selectedPlan) : null,
    sortOrder
  );
  const { data: plans } = useAdminPlans();
  const { data: me } = useAdminMe();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data: detail } = useAdminTicket(selectedId);
  const reply = useAdminReplyTicket(selectedId);
  const close = useAdminCloseTicket(selectedId);
  const reopen = useAdminReopenTicket(selectedId);
  const [replyText, setReplyText] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const overlayMouseDown = useRef(false);

  const canManage = Boolean(me?.is_admin) || Boolean(me?.permissions?.includes("tickets.manage"));
  const [filter, setFilter] = useState<"all" | "open" | "closed">("all");

  // Debounce the search input so a refetch fires only after the user pauses.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredTickets = (tickets as Record<string, unknown>[] | undefined)?.filter((t) => {
    if (filter === "open") return t.status !== "closed";
    if (filter === "closed") return t.status === "closed";
    return true;
  });

  // Plan options come from the plans catalogue; when the staffer cannot
  // list plans (no subscriptions.view permission) fall back to the plans
  // observed in the returned tickets so the filter stays usable.
  const cataloguePlans = (plans as Record<string, unknown>[] | undefined) || [];
  const planOptions: { plan_id: number; name: string; monthly_price?: unknown }[] =
    cataloguePlans.length > 0
      ? (cataloguePlans as { plan_id: number; name: string; monthly_price?: unknown }[])
      : (() => {
          const seen = new Map<number, string>();
          for (const t of (tickets as Record<string, unknown>[] | undefined) || []) {
            const pid = t.plan_id as number | null;
            if (pid !== null && pid !== undefined && !seen.has(pid)) {
              seen.set(pid, (t.plan_name as string) || `#${pid}`);
            }
          }
          return [...seen.entries()].map(([plan_id, name]) => ({ plan_id, name }));
        })();

  const hasActiveFilters = Boolean(search || selectedPlan || sortOrder !== "newest");

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setSelectedPlan("");
    setSortOrder("newest");
  };

  const handleReply = () => {
    if (!replyText.trim() || selectedId === null) return;
    reply.mutate({ message: replyText }, { onSuccess: () => setReplyText("") });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>تیکت‌ها</h1>
          <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>مدیریت تیکت‌های پشتیبانی</p>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center w-full sm:w-auto">
          <div className="relative w-full sm:w-auto">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="جستجوی کاربر (نام / ایمیل / تلفن)..."
              className="admin-input w-full sm:w-[220px]"
              style={{ paddingLeft: 34 }}
            />
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--admin-text-tertiary)",
                pointerEvents: "none",
              }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          {/* Plan Filter */}
          <select
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)}
            className="admin-input w-full sm:w-[170px]"
            title="فیلتر بر اساس پلن کاربر"
          >
            <option value="">همه پلن‌ها</option>
            {planOptions.map((plan) => {
              const isFree = plan.monthly_price !== undefined && Number(plan.monthly_price) === 0;
              return (
                <option key={plan.plan_id} value={String(plan.plan_id)}>
                  {plan.name}
                  {isFree ? " (رایگان)" : ""}
                </option>
              );
            })}
          </select>

          {/* Sort: ticket priority (ties → user plan priority → date) or date */}
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="admin-input w-full sm:w-[210px]"
            title="در مرتب‌سازی بر اساس اولویت، تیکت‌های هم‌اولویت بر اساس سطح پلن کاربر (بالاتر اول) و سپس تاریخ مرتب می‌شوند"
          >
            <option value="newest">جدیدترین (تاریخ ↓)</option>
            <option value="oldest">قدیمی‌ترین (تاریخ ↑)</option>
            <option value="priority_desc">اولویت تیکت: زیاد به کم</option>
            <option value="priority_asc">اولویت تیکت: کم به زیاد</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="admin-btn admin-btn-secondary admin-btn-sm w-full sm:w-auto"
            >
              بازنشانی فیلترها
            </button>
          )}

          <div className="flex items-center gap-2 bg-white p-1 rounded-2xl w-full sm:w-auto" style={{ border: "1px solid var(--admin-border)" }}>
          {([
            { key: "all", label: "همه" },
            { key: "open", label: "باز" },
            { key: "closed", label: "بسته شده" },
          ] as const).map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`admin-btn admin-btn-sm flex-1 sm:flex-none ${filter === f.key ? "admin-btn-primary" : "admin-btn-secondary"}`}
            >
              {f.label}
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Tickets List / Empty State */}
      <div className="admin-card">
        {isLoading ? (
          <div className="space-y-3 p-6">
            <div className="h-14 bg-surface-hover rounded animate-pulse" />
            <div className="h-14 bg-surface-hover rounded animate-pulse" />
            <div className="h-14 bg-surface-hover rounded animate-pulse" />
          </div>
        ) : (filteredTickets || []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: "var(--admin-primary-light)" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--admin-primary)" strokeWidth="1.5" strokeLinecap="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                <line x1="9" y1="10" x2="15" y2="10" />
                <line x1="12" y1="7" x2="12" y2="13" />
              </svg>
            </div>
            <h3 className="text-base font-semibold mb-1" style={{ color: "var(--admin-text-primary)" }}>هیچ تیکتی وجود ندارد</h3>
            <p className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>
              تیکت‌های پشتیبانی کاربران در این بخش نمایش داده می‌شوند
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>شماره تیکت</th>
                  <th>موضوع</th>
                  <th>کاربر</th>
                  <th>پلن</th>
                  <th>وضعیت</th>
                  <th>اولویت</th>
                  <th>تاریخ</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {(filteredTickets as Record<string, unknown>[]).map((ticket) => {
                  const sb = statusBadge[ticket.status as string] || statusBadge.open;
                  const tNum = String((ticket.ticket_number as string) ?? String(ticket.id).padStart(4,'0').slice(0,8));
                  const userLabel = userDisplayLabel(ticket);
                  const userContacts = [ticket.user_phone, ticket.user_email].filter(Boolean).join(" · ");
                  return (
                    <tr key={ticket.id as string}>
                      <td>
                        <span className="font-mono text-xs px-2 py-0.5 rounded" style={{ background: "var(--admin-primary-light)", color: "var(--admin-primary)", border: "1px solid var(--admin-border)" }}>
                          #{tNum}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>
                          {ticket.subject as string}
                        </span>
                      </td>
                      <td title={userContacts || userLabel}>{userLabel}</td>
                      <td>
                        {(ticket.plan_name as string) ? (
                          <span className="inline-flex items-center gap-1">
                            <span>{ticket.plan_name as string}</span>
                            {ticket.plan_priority_level !== null && ticket.plan_priority_level !== undefined && (
                              <span className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>
                                (سطح {toPersianDigits(String(ticket.plan_priority_level))})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: "var(--admin-text-tertiary)" }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={`admin-badge ${sb.cls}`}>
                          {sb.label}
                        </span>
                      </td>
                      <td>{priorityLabel[ticket.priority as string] || ticket.priority as string}</td>
                      <td>{new Date(ticket.created_at as string).toLocaleDateString("fa-IR")}</td>
                      <td>
                        <button
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                          onClick={() => {
                            setSelectedId(Number(ticket.id));
                            setReplyText("");
                          }}
                        >
                          مشاهده
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ticket detail drawer */}
      {selectedId !== null && (
        <div
          className="admin-overlay"
          role="presentation"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setSelectedId(null); overlayMouseDown.current = false; }}
        >
          <aside
            className="admin-drawer-panel absolute inset-y-0 left-0 w-full overflow-y-auto p-4 sm:p-6 md:w-2/3"
            role="dialog"
            aria-modal="true"
            aria-label="جزئیات تیکت"
            onClick={(e) => e.stopPropagation()}
          >
            {!detail ? (
              <div className="flex items-center justify-center py-16">
                <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                        {detail.subject}
                      </h2>
                      <span className="font-mono text-xs px-2 py-0.5 rounded" style={{ background: "var(--admin-primary-light)", color: "var(--admin-primary)", border: "1px solid var(--admin-border)" }}>
                        #{String((detail.ticket_number as string) ?? String(detail.id).padStart(4,'0').slice(0,8))}
                      </span>
                      {(statusBadge[detail.status] || statusBadge.open) && (
                        <span className={`admin-badge ${(statusBadge[detail.status] || statusBadge.open).cls}`}>
                          {(statusBadge[detail.status] || statusBadge.open).label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                      <span>{userDisplayLabel(detail as Record<string, unknown>)}</span>
                      <span>•</span>
                      <span>{categoryLabel[detail.category] || detail.category}</span>
                      <span>•</span>
                      <span>اولویت: {priorityLabel[detail.priority] || detail.priority}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedId(null)}
                    className="p-1 rounded-lg transition-colors"
                    style={{ color: "var(--admin-text-tertiary)" }}
                    aria-label="بستن جزئیات تیکت"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>

                {/* Sender profile */}
                <div
                  className="rounded-xl p-3 mb-4 text-xs space-y-2"
                  style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold" style={{ color: "var(--admin-text-secondary)" }}>
                      اطلاعات کاربر
                    </p>
                    <span style={{ color: "var(--admin-text-tertiary)" }}>
                      شناسه: {toPersianDigits(String(detail.user_id))}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    <UserInfoRow label="نام" value={detail.user_name as string} />
                    <UserInfoRow label="نام خانوادگی" value={detail.user_family_name as string} />
                    <UserInfoRow label="شماره تماس" value={detail.user_phone as string} />
                    <UserInfoRow label="ایمیل" value={detail.user_email as string} />
                    <UserInfoRow
                      label="موجودی کیف پول"
                      value={`${formatTomanInt(detail.user_wallet_balance as string | number)} تومان`}
                    />
                    <UserInfoRow label="پلن فعال" value={detail.user_active_plan as string} />
                    <UserInfoRow
                      label="مصرف کل"
                      value={detail.user_total_usage !== undefined && detail.user_total_usage !== null
                        ? `${formatTomanInt(detail.user_total_usage as string | number)} تومان`
                        : "—"}
                    />
                    <UserInfoRow
                      label="تاریخ ثبت‌نام"
                      value={detail.user_created_at
                        ? new Date(detail.user_created_at as string).toLocaleDateString("fa-IR")
                        : ""}
                    />
                  </div>
                </div>

                {/* Messages */}
                <div className="space-y-4 max-h-[380px] overflow-y-auto pr-1 mb-4">
                  {(detail.messages || []).map((m: { message_id: number; content: string; is_admin_reply: boolean; created_at: string; attachments?: { attachment_id: number; original_name: string; size_bytes: number; scan_status: string }[] }) => (
                    <div key={m.message_id} className={`flex ${m.is_admin_reply ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                          m.is_admin_reply
                            ? ""
                            : ""
                        }`}
                        style={
                          m.is_admin_reply
                            ? { background: "var(--admin-bg-hover)", color: "var(--admin-text-primary)", border: "1px solid var(--admin-border)" }
                            : { background: "var(--admin-primary)", color: "#fff" }
                        }
                      >
                        <TicketMessageContent content={m.content} />
                        {m.attachments && m.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {m.attachments.map((a) => {
                              const blocked = a.scan_status === "infected";
                              const inner = (
                                <>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                  </svg>
                                  <span className="max-w-[160px] truncate">{a.original_name}</span>
                                  {a.scan_status === "infected" && (
                                    <span className="text-[10px] px-1 rounded bg-red-500/20 text-red-500 shrink-0">آلوده</span>
                                  )}
                                  {a.scan_status === "pending" && (
                                    <span className="text-[10px] px-1 rounded bg-amber-500/20 shrink-0">در بررسی</span>
                                  )}
                                </>
                              );
                              if (blocked) {
                                return (
                                  <span
                                    key={a.attachment_id}
                                    className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-tiny opacity-80 ${m.is_admin_reply ? "bg-surface-muted border border-line" : "bg-black/20 border border-white/10"}`}
                                    style={m.is_admin_reply ? { color: "var(--admin-text-tertiary)" } : { color: "rgba(255,255,255,0.7)" }}
                                  >
                                    {inner}
                                  </span>
                                );
                              }
                              return (
                                <a
                                  key={a.attachment_id}
                                  href={supportApi.getAttachmentDownloadUrl(Number(detail.id), a.attachment_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-tiny transition-colors no-underline"
                                  style={m.is_admin_reply
                                    ? { background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)", color: "var(--admin-text-secondary)" }
                                    : { background: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
                                >
                                  {inner}
                                </a>
                              );
                            })}
                          </div>
                        )}
                        <p
                          className="text-tiny mt-1.5"
                          style={m.is_admin_reply ? { color: "var(--admin-text-tertiary)" } : { color: "rgba(255,255,255,0.7)" }}
                        >
                          {m.is_admin_reply ? "پشتیبانی" : "کاربر"} • {timeAgo(m.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="border-t pt-4 space-y-3" style={{ borderColor: "var(--admin-border)" }}>
                  {detail.status !== "closed" ? (
                    <>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="پاسخ خود را بنویسید..."
                        className="admin-input"
                        style={{ height: "auto", minHeight: 80, paddingTop: 12, paddingBottom: 12, resize: "vertical" }}
                        disabled={!canManage}
                      />
                      <div className="flex items-center gap-2">
                        <button
                          className="admin-btn admin-btn-primary"
                          disabled={!canManage || !replyText.trim() || reply.isPending}
                          onClick={handleReply}
                        >
                          {reply.isPending ? "در حال ارسال..." : "ارسال پاسخ"}
                        </button>
                        {canManage && (
                          <button
                            className="admin-btn admin-btn-secondary"
                            onClick={() => setConfirmClose(true)}
                          >
                            بستن تیکت
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>
                        این تیکت بسته شده است
                      </p>
                      {canManage && (
                        <button
                          className="admin-btn admin-btn-secondary"
                          disabled={reopen.isPending}
                          onClick={() => reopen.mutate()}
                        >
                          باز کردن تیکت
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </aside>
        </div>
      )}

      {/* Close ticket confirmation modal */}
      {confirmClose && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setConfirmClose(false); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-2" style={{ color: "var(--admin-text-primary)" }}>
              بستن تیکت
            </h3>
            <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--admin-text-secondary)" }}>
              آیا از بستن این تیکت مطمئن هستید؟ پس از بستن، تیکت برای پاسخ‌دهی جدید بسته شده و دیگر امکان ارسال پیام وجود ندارد.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button className="admin-btn admin-btn-secondary" onClick={() => setConfirmClose(false)}>
                انصراف
              </button>
              <button
                className="admin-btn admin-btn-primary"
                disabled={close.isPending}
                onClick={() => close.mutate(undefined, { onSuccess: () => setConfirmClose(false) })}
              >
                {close.isPending ? "در حال بستن..." : "بستن تیکت"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
